import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  XCircle,
  Eye,
  Percent,
  Store,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import AdminGroupedPaymentsTable from '../../components/admin/AdminGroupedPaymentsTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminTransactionsData } from '../../hooks/useAdminTransactions';
import { formatDate } from '../../utils/dateUtils';
import {
  formatAdminMoney,
  getVendorPayout,
  getPlatformCommission,
  groupPaymentsByBooking,
} from '../../utils/adminPaymentTotals';
import PrivateBookingBadge from '../../components/shared/PrivateBookingBadge';
import { isPrivateBooking, PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../utils/bookingSource';

const AdminTransactions = () => {
  const navigate = useNavigate();

  const {
    transactions,
    isLoading,
    error,
    filters,
    statusOptions,
    handleFilterChange,
    getStatusConfig,
    handleSearch,
    statistics,
  } = useAdminTransactionsData();

  const transactionList = useMemo(() => {
    const allTransactions = Array.isArray(transactions) ? transactions : transactions?.data || [];
    const filtered = allTransactions.filter((transaction) => transaction.status !== 'pending');
    return groupPaymentsByBooking(filtered);
  }, [transactions]);

  const columns = [
    {
      key: 'order',
      label: 'Order',
      sortable: true,
      width: 'w-44',
      render: (transaction) => {
        const bookingRef = transaction.booking?.booking_reference || transaction.booking_id || 'N/A';
        const vendorName = transaction.booking?.vendor?.business_name || '—';
        return (
          <div className="min-w-0">
            {transaction.booking_id ? (
              <button
                type="button"
                onClick={() => navigate(`/admin/bookings/${transaction.booking_id}`)}
                className="text-sm font-medium text-primary-600 hover:underline truncate block"
              >
                #{bookingRef}
              </button>
            ) : (
              <span className="text-sm text-gray-400">N/A</span>
            )}
            {isPrivateBooking(transaction) && (
              <div className="mt-1">
                <PrivateBookingBadge />
              </div>
            )}
            <p className="text-xs text-gray-500 truncate">{vendorName}</p>
          </div>
        );
      },
    },
    {
      key: 'amount',
      label: 'Paid',
      sortable: true,
      width: 'w-28',
      render: (transaction) => {
        const amount = parseFloat(transaction.amount || 0);
        const isRefund = transaction.type === 'refund' || transaction.status === 'refunded';
        return (
          <div className={`text-right ${isRefund ? 'text-red-600' : 'text-gray-900'}`}>
            <p className="text-sm font-medium">
              {isRefund ? '-' : ''}${formatAdminMoney(Math.abs(amount))}
            </p>
            {transaction.payment_type && (
              <p className="text-xs text-gray-500">{transaction.payment_type}</p>
            )}
          </div>
        );
      },
    },
    {
      key: 'platform_fee',
      label: 'Commission',
      sortable: true,
      width: 'w-24',
      render: (transaction) => {
        const isRefund = transaction.type === 'refund' || transaction.status === 'refunded';
        if (isRefund) {
          return <p className="text-sm text-right text-gray-400">—</p>;
        }
        if (isPrivateBooking(transaction)) {
          return (
            <div className="text-right">
              <p className="text-sm text-gray-400">$0.00</p>
              <p className="text-xs text-indigo-600">{PRIVATE_PAYOUT_EXCLUDED_NOTE}</p>
            </div>
          );
        }
        const fee = getPlatformCommission(transaction);
        return (
          <p className="text-sm font-medium text-right text-indigo-600">
            {fee > 0 ? `-$${formatAdminMoney(fee)}` : '$0.00'}
          </p>
        );
      },
    },
    {
      key: 'vendor_payout',
      label: 'To vendor',
      sortable: true,
      width: 'w-28',
      render: (transaction) => {
        const isRefund = transaction.type === 'refund' || transaction.status === 'refunded';
        if (isRefund) {
          return <p className="text-sm text-right text-gray-400">—</p>;
        }
        if (isPrivateBooking(transaction)) {
          return (
            <div className="text-right">
              <p className="text-sm text-gray-400">$0.00</p>
              <p className="text-xs text-gray-500">{PRIVATE_PAYOUT_EXCLUDED_NOTE}</p>
            </div>
          );
        }
        const payout = getVendorPayout(transaction);
        return (
          <p
            className={`text-sm font-medium text-right ${
              payout > 0 ? 'text-emerald-600' : 'text-gray-400'
            }`}
          >
            ${formatAdminMoney(payout)}
          </p>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 'w-28',
      render: (transaction) => (
        <span className="text-sm text-gray-700">{formatDate(transaction.created_at, 'medium')}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-28',
      render: (transaction) => {
        const statusConfig = getStatusConfig(transaction.status);
        const StatusIcon = statusConfig.icon;
        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}
          >
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusConfig.label}
          </span>
        );
      },
    },
  ];

  const actions = [
    {
      label: 'View Booking',
      icon: Eye,
      variant: 'default',
      onClick: (transaction) => {
        if (transaction.booking_id) {
          navigate(`/admin/bookings/${transaction.booking_id}`);
        }
      },
    },
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Transactions</h3>
          <p className="text-gray-600">
            {error?.response?.data?.message || 'Failed to load transactions. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6" />
            Transactions
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Client payments, platform commission, and vendor share. Totals exclude private bookings.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <p className="text-sm text-gray-600">Total collected</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              ${formatAdminMoney(statistics.totalInflow)}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Platform bookings only</p>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">Commission (deduction)</p>
                <p className="text-2xl font-bold text-indigo-600 mt-1">
                  -${formatAdminMoney(statistics.accumulatedCommission)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Excludes private bookings</p>
              </div>
              <Percent className="w-8 h-8 text-indigo-500 flex-shrink-0" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">To vendors</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  ${formatAdminMoney(statistics.totalVendorPayout)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Excludes private bookings</p>
              </div>
              <Store className="w-8 h-8 text-emerald-500 flex-shrink-0" />
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SearchSelect
              label="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              options={statusOptions}
            />
            <DatePicker
              label="From Date"
              value={filters.date_from}
              onChange={(value) => handleFilterChange('date_from', value)}
            />
            <DatePicker
              label="To Date"
              value={filters.date_to}
              onChange={(value) => handleFilterChange('date_to', value)}
            />
          </div>
        </div>

        <div className="bg-white border rounded-lg p-3 sm:p-4">
          {/* Grouped by booking with expandable payment rows */}
          <AdminGroupedPaymentsTable
            data={transactionList}
            columns={columns}
            actions={actions}
            loading={isLoading}
            getStatusConfig={getStatusConfig}
            searchPlaceholder="Search by booking or vendor..."
            onSearch={handleSearch}
            emptyMessage="No transactions found"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminTransactions;
